import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtPHP, fmtDateTime, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, Receipt } from "lucide-react";

export default function Repayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments");
        setPayments(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const total = payments.reduce((s, p) => s + p.amount, 0);

  return (
    <div className="space-y-6" data-testid="payments-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-400 font-bold">
            Ledger
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white mt-1">
            Repayments
          </h1>
          <p className="text-slate-400 text-sm mt-1 font-mono">
            {payments.length} transactions · {fmtPHP(total)} collected
          </p>
        </div>
        <a
          href={`${API_BASE}/export/payments`}
          data-testid="export-payments-csv"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-sm hover:bg-emerald-500/20"
        >
          <Download className="h-4 w-4" /> Export CSV
        </a>
      </header>

      <Card className="card-glow rounded-xl bg-[#111826] border border-white/5 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="text-xs uppercase text-slate-400">Receipt</TableHead>
              <TableHead className="text-xs uppercase text-slate-400">Borrower</TableHead>
              <TableHead className="text-xs uppercase text-slate-400 text-right">Amount</TableHead>
              <TableHead className="text-xs uppercase text-slate-400">Method</TableHead>
              <TableHead className="text-xs uppercase text-slate-400">Reference</TableHead>
              <TableHead className="text-xs uppercase text-slate-400">Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 py-10">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 py-16">
                  <Receipt className="h-8 w-8 mx-auto text-slate-700 mb-3" />
                  No repayments yet.
                </TableCell>
              </TableRow>
            )}
            {payments.map((p) => (
              <TableRow
                key={p.id}
                data-testid={`ledger-row-${p.id}`}
                onClick={() => navigate(`/loans/${p.loan_id}`)}
                className="border-white/5 hover:bg-emerald-500/5 cursor-pointer"
              >
                <TableCell className="font-mono text-xs text-slate-300">{p.receipt_no}</TableCell>
                <TableCell className="font-semibold text-white">{p.borrower_name}</TableCell>
                <TableCell className="font-mono tabular text-right text-emerald-300 font-semibold">
                  {fmtPHP(p.amount)}
                </TableCell>
                <TableCell className="text-slate-300">{p.method}</TableCell>
                <TableCell className="text-slate-400 font-mono text-xs">
                  {p.reference || "—"}
                </TableCell>
                <TableCell className="text-slate-400 text-sm">{fmtDateTime(p.paid_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
